import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, notifyError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  BellRing,
  Phone,
  Loader2,
  MessageSquare,
  AlertTriangle,
  Boxes,
} from "lucide-react";

function daysFromNow(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

function seasonLabel(s) {
  if (s === "yaz") return "Yaz";
  if (s === "kis") return "Kış";
  if (s === "dort_mevsim") return "4 Mevsim";
  return s || "—";
}

export default function Reminders() {
  const { salesStockEnabled } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  // Kritik stok uyarıları (şartname #21) — yalnızca modül aktifse yüklenir.
  const [criticalStock, setCriticalStock] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reminders?days=60");
        setList(data);
      } catch (e) {
        notifyError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!salesStockEnabled) {
      setCriticalStock([]);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/sales-stock/products/critical");
        setCriticalStock(data);
      } catch (e) {
        // Modül erişimi kapanmışsa sessizce geç — emanet hatırlatmaları çalışsın
        if (e?.response?.status !== 403) notifyError(e);
      }
    })();
  }, [salesStockEnabled]);

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-6" data-testid="reminders-page">
      <div>
        <div className="label-eyebrow text-amber-600">Yaklaşan</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">Hatırlatmalar</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Önümüzdeki 60 gün içinde teslim alınması beklenen lastikler
          {salesStockEnabled ? " ve kritik seviyeye düşen stoklar." : "."}
        </p>
      </div>

      {/* Kritik stok uyarısı — şartname #21 */}
      {salesStockEnabled && criticalStock.length > 0 && (
        <Card
          className="rounded-sm border-red-500/40 bg-red-50/40 overflow-hidden"
          data-testid="critical-stock-section"
        >
          <div className="flex items-center gap-3 p-5 border-b border-red-500/30">
            <div className="h-10 w-10 bg-red-600 text-white rounded-sm flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="label-eyebrow text-red-700">Kritik Stok</div>
              <h3 className="font-display font-bold text-lg tracking-tight">
                {criticalStock.length} ürün kritik seviyede
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Mevcut stok, belirlediğiniz kritik stok miktarına eşit veya altında.
              </p>
            </div>
            <Link
              to="/satis-stok"
              className="inline-flex items-center gap-1.5 h-9 px-3 border border-red-500/40 rounded-sm text-sm font-semibold text-red-700 hover:bg-red-100"
              data-testid="critical-stock-link"
            >
              <Boxes className="h-4 w-4" /> Satış/Stok
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-red-100/60">
                <tr>
                  <th className="text-left px-4 py-2 label-eyebrow">Ürün</th>
                  <th className="text-left px-4 py-2 label-eyebrow">Tip</th>
                  <th className="text-left px-4 py-2 label-eyebrow">Depo</th>
                  <th className="text-right px-4 py-2 label-eyebrow">Stok</th>
                  <th className="text-right px-4 py-2 label-eyebrow">Kritik</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-red-500/15">
                {criticalStock.map((p) => (
                  <tr key={p.id} data-testid={`critical-stock-row-${p.id}`}>
                    <td className="px-4 py-2 font-semibold">{p.name}</td>
                    <td className="px-4 py-2 text-xs">{p.product_type_label}</td>
                    <td className="px-4 py-2 text-xs font-mono">{p.warehouse_name || "—"}</td>
                    <td className="px-4 py-2 font-mono text-right font-bold text-red-700">
                      {p.quantity}
                    </td>
                    <td className="px-4 py-2 font-mono text-right">{p.critical_quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : list.length === 0 ? (
        <Card className="p-12 text-center rounded-sm">
          <BellRing className="h-10 w-10 mx-auto text-muted-foreground" />
          <div className="font-display font-bold text-lg mt-3">Yaklaşan hatırlatma yok</div>
          <p className="text-sm text-muted-foreground mt-2">
            Emanet kaydederken "Tahmini Teslim Tarihi" girerseniz burada listelenecek.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((s) => {
            const days = daysFromNow(s.expected_pickup_date);
            const urgent = days <= 7;
            return (
              <Card key={s.id} className={`surface-card rounded-sm p-5 ${urgent ? "border-amber-500" : ""}`} data-testid={`reminder-card-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-lg tracking-tight truncate">
                      {s.customer_name}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
                      <Phone className="h-3.5 w-3.5" />
                      <a href={`tel:${s.customer_phone}`} className="font-mono hover:text-foreground">
                        {s.customer_phone}
                      </a>
                    </div>
                  </div>
                  <Badge className={`rounded-sm ${urgent ? "bg-amber-500 text-foreground hover:bg-amber-500" : "bg-foreground text-background hover:bg-foreground"}`}>
                    {days <= 0 ? "GECİKMİŞ" : `${days} GÜN`}
                  </Badge>
                </div>
                <div className="mt-4 pt-4 border-t border-border space-y-1 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Lastik</span>
                    <span className="font-mono">{s.brand} • {s.size}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Sezon</span>
                    <span className="font-semibold">{seasonLabel(s.season)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Depo</span>
                    <span className="font-mono">{s.warehouse_name || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Raf</span>
                    <span className="font-mono">{s.location || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Beklenen</span>
                    <span className="font-mono">{new Date(s.expected_pickup_date).toLocaleDateString("tr-TR")}</span>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <a
                    href={`https://wa.me/${(s.customer_phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(
                      `Merhaba ${s.customer_name}, lastik oteli randevunuz yaklaşıyor. Lastikleriniz (${s.brand} ${s.size}) hazır. Detay için bize ulaşabilirsiniz.`
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 h-10 bg-foreground text-background rounded-sm text-sm font-semibold hover:bg-foreground/90"
                    data-testid={`whatsapp-${s.id}`}
                  >
                    <MessageSquare className="h-4 w-4" /> WhatsApp
                  </a>
                  <Link
                    to={`/emanetler/${s.id}`}
                    className="flex-1 inline-flex items-center justify-center h-10 border border-border rounded-sm text-sm font-semibold hover:bg-muted"
                    data-testid={`open-${s.id}`}
                  >
                    Detay
                  </Link>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
